import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { AuthLayout } from "@/components/AuthLayout";
import { AUTH } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!token) {
      setError("Missing or invalid reset token.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setOk(true);
      toast.success("Password reset. You can now log in.");
      setTimeout(() => navigate("/login", { replace: true }), 900);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Set a new password"
      subtitle="Choose a strong password. This link expires in 1 hour."
      hint={<Link to="/login" className="font-semibold text-[#101876] hover:underline">Back to login</Link>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid={AUTH.resetRoot}>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        {ok && <Alert data-testid={AUTH.resetSuccess} className="border-emerald-200 bg-emerald-50 text-emerald-800"><AlertDescription>Password updated. Redirecting…</AlertDescription></Alert>}
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">New password</Label>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid={AUTH.resetPassword} />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Confirm password</Label>
          <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid={AUTH.resetConfirm} />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#101876] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#0c135c] disabled:opacity-70"
          data-testid={AUTH.resetSubmit}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
          {loading ? "Updating…" : "Update password"}
        </button>
      </form>
    </AuthLayout>
  );
}
